// TokenModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
    Dialog,
} from "@mui/material";
import { IoClose, IoSearch } from "react-icons/io5";
import { list } from "../contexts/SelectTokenDATA";
import { NETWORK_LABEL } from "../contexts/config";
import { getCurrencyMetadata, balanceOf } from "thirdweb/extensions/erc20";
import { getErc20Contract } from "../contexts/thirdWebClient";
import {
    useActiveAccount,
    useActiveWalletChain,
} from "thirdweb/react";
import { toast } from "react-toastify";
import { ethers } from "ethers";

const TokenModal = ({ open, setOpen, onClick, excludeToken }) => {
    const tokens = list.filter(t => !excludeToken || t.address !== excludeToken.address);
    const [contractAddress, setContractAddress] = useState("");
    const [tokenDetals, setToknDetails] = useState({});
    const [balances, setBalances] = useState({});

    const add = useActiveAccount();
    const [userAddress, setAddress] = useState("");

    const chain = useActiveWalletChain();
    useEffect(() => {
        if (add && add.address) {
            setAddress(add.address);
        }
    }, [add, chain]);

    // Fetch balances for all tokens when modal opens
    const fetchBalances = useCallback(async () => {
        if (!userAddress || !chain?.id) return;
        const newBalances = {};
        for (const token of tokens) {
            try {
                const contract = getErc20Contract(token.address, chain);
                const bal = await balanceOf({ contract, address: userAddress });
                newBalances[token.address.toLowerCase()] = ethers.formatUnits(bal, token.decimals || 18);
            } catch {
                newBalances[token.address.toLowerCase()] = "0";
            }
        }
        setBalances(newBalances);
    }, [userAddress, chain, tokens]);

    useEffect(() => {
        if (open && userAddress) {
            fetchBalances();
        }
    }, [open, userAddress, fetchBalances]);

    // Clear search when modal closes
    useEffect(() => {
        if (!open) {
            setContractAddress("");
            setToknDetails({});
        }
    }, [open]);

    const isValidAddress = (addr) => /^0x[a-fA-F0-9]{40}$/.test(addr);

    const tokenDetailsLookup = async (value) => {
        try {
            if (value && isValidAddress(value) && chain && chain?.id) {
                const contract = getErc20Contract(value, chain);
                let currencyMetadata = await getCurrencyMetadata({ contract });
                currencyMetadata.address = value;
                setToknDetails(currencyMetadata);
            } else {
                setToknDetails({});
            }
        } catch (error) {
            toast.error("Invalid token address or token not found on this network");
            console.log("%c Line:53 🍎 error", "color:#6ec1c2", error);
        }
    };

    const selectToken = () => {
        if (excludeToken && tokenDetals?.address?.toLowerCase() === excludeToken?.address?.toLowerCase()) {
            toast.error("Cannot select the same token for both fields");
            return;
        }
        onClick(tokenDetals);
        setOpen(false);
        setContractAddress("");
        setToknDetails({});
    };

    const selectNativeToken = (data) => {
        onClick(data);
        setOpen(false);
        setContractAddress("");
        setToknDetails({});
    };

    // Filter tokens by name or symbol
    const searchTerm = contractAddress.trim().toLowerCase();
    const isAddressSearch = isValidAddress(contractAddress.trim());
    const filteredTokens = searchTerm && !isAddressSearch
        ? tokens.filter(t =>
            t.name.toLowerCase().includes(searchTerm) ||
            t.symbol.toLowerCase().includes(searchTerm)
        )
        : tokens;

    const formatBalance = (bal) => {
        if (!bal || bal === "0") return "0";
        const num = parseFloat(bal);
        if (num === 0) return "0";
        if (num < 0.0001) return "<0.0001";
        if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
        if (num >= 1000) return (num / 1000).toFixed(2) + "K";
        return num.toFixed(4);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setContractAddress(value);
        // Only do address lookup if it's a valid address
        if (isValidAddress(value.trim())) {
            tokenDetailsLookup(value.trim());
        } else {
            setToknDetails({});
        }
    };

    // Show address search result
    const showAddressResult = isAddressSearch && tokenDetals && tokenDetals?.address;

    const shortAddress = (a) =>
        a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "";

    /* Display label only — see the warning on NETWORK_LABEL in contexts/config.js.
       Deliberately not `chain?.name`, which would report the real chain. */
    const networkName = NETWORK_LABEL;

    return (
        <Dialog open={open} onClose={() => setOpen(!open)} maxWidth="xs" fullWidth
            PaperProps={{
                className: "!rounded-[24px]",
                style: { background: 'none', boxShadow: 'none' }
            }}
        >
            <div className="card-3d modal-shell">

                {/* Pinned chrome: title, close, and the search field */}
                <div className="modal-head">
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <h2 className="text-[18px] sm:text-[20px] font-bold gradient-text">
                            Select Token
                        </h2>
                        <button
                            type="button"
                            onClick={() => setOpen(!open)}
                            aria-label="Close token selector"
                            className="swap-arrow-3d w-9 h-9 flex items-center justify-center shrink-0"
                        >
                            <IoClose className="size-[18px]" />
                        </button>
                    </div>

                    <div className="inner-3d flex items-center gap-2.5 px-3.5">
                        <IoSearch className="size-[16px] text-usdx-ink-subtle shrink-0" aria-hidden="true" />
                        <input
                            value={contractAddress}
                            onChange={handleSearchChange}
                            id="search"
                            placeholder="Search name or symbol, or paste an address"
                            type="text"
                            autoComplete="off"
                            spellCheck="false"
                            aria-label="Search tokens by name or contract address"
                            className="w-full min-w-0 py-3 bg-transparent text-usdx-ink placeholder:text-usdx-ink-subtle/60 outline-none text-[14px] font-medium"
                        />
                        {contractAddress && (
                            <button
                                type="button"
                                onClick={() => { setContractAddress(""); setToknDetails({}); }}
                                aria-label="Clear search"
                                className="shrink-0 text-usdx-ink-subtle hover:text-usdx-accent transition-colors"
                            >
                                <IoClose className="size-[16px]" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Only this region scrolls. Capped so a long list scrolls, but a
                    short one does not leave dead space below it. */}
                <div className="modal-body max-h-[min(52dvh,340px)]">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                        <p className="text-[11px] font-semibold text-usdx-ink-subtle uppercase tracking-widest">
                            {showAddressResult
                                ? "Imported token"
                                : searchTerm
                                ? "Results"
                                : "Popular tokens"}
                        </p>
                        {userAddress && !showAddressResult && (
                            <p className="text-[10px] font-medium text-usdx-ink-subtle uppercase tracking-wider">
                                Balance
                            </p>
                        )}
                    </div>

                    {showAddressResult ? (
                        <button
                            type="button"
                            onClick={() => selectToken()}
                            className="token-row"
                        >
                            <div className="w-9 h-9 rounded-full bg-usdx-accent/15 flex items-center justify-center shrink-0">
                                <span className="text-usdx-accent text-[13px] font-bold">
                                    {tokenDetals?.symbol?.charAt(0)}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-usdx-ink text-[15px] font-bold truncate">
                                    {tokenDetals?.symbol}
                                </p>
                                <p className="num text-usdx-ink-subtle text-[11px] truncate">
                                    {shortAddress(tokenDetals?.address)}
                                </p>
                            </div>
                            <span className="text-[11px] font-bold text-usdx-accent uppercase tracking-wider shrink-0">
                                Import
                            </span>
                        </button>
                    ) : filteredTokens.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                            {filteredTokens.map((token) => {
                                const bal = balances[token.address.toLowerCase()];
                                return (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            selectNativeToken(token);
                                            setOpen(false);
                                        }}
                                        key={token.address}
                                        className="token-row"
                                    >
                                        {token?.img ? (
                                            <img
                                                src={token?.img}
                                                alt=""
                                                className="size-[34px] rounded-full ring-1 ring-usdx-line shrink-0"
                                            />
                                        ) : (
                                            <div className="size-[34px] rounded-full bg-usdx-accent/15 flex items-center justify-center shrink-0">
                                                <span className="text-usdx-accent text-[13px] font-bold">
                                                    {token?.symbol?.charAt(0)}
                                                </span>
                                            </div>
                                        )}
                                        {/* Symbol leads — it is what people scan for */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-usdx-ink text-[15px] font-bold truncate">
                                                {token?.symbol}
                                            </p>
                                            <p className="text-usdx-ink-subtle text-[12px] truncate">
                                                {token?.name}
                                            </p>
                                        </div>
                                        {userAddress && (
                                            <p className="num text-usdx-ink text-[14px] font-medium shrink-0">
                                                {bal !== undefined ? formatBalance(bal) : "·"}
                                            </p>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                            <div className="w-11 h-11 rounded-full bg-usdx-surface-sunken flex items-center justify-center mb-1">
                                <IoSearch className="size-[18px] text-usdx-ink-subtle" aria-hidden="true" />
                            </div>
                            <p className="text-usdx-ink text-[14px] font-semibold">No tokens found</p>
                            <p className="text-usdx-ink-subtle text-[12px] max-w-[240px] leading-relaxed">
                                Paste the token's contract address above to import it.
                            </p>
                        </div>
                    )}
                </div>

                {/* Pinned footer — carries the network the list belongs to */}
                <div className="modal-foot flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 min-w-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-usdx-success shrink-0" aria-hidden="true" />
                        <span className="text-[11px] font-medium text-usdx-ink-subtle truncate">
                            {networkName}
                        </span>
                    </span>
                    <span className="num text-[11px] font-medium text-usdx-ink-subtle shrink-0">
                        {filteredTokens.length} {filteredTokens.length === 1 ? "token" : "tokens"}
                    </span>
                </div>
            </div>
        </Dialog>
    );
};

export default TokenModal;
